import React, { FC, useEffect, useRef, useState } from 'react';
import { FlightFilterModel } from 'ts/flight/classes/FlightFilterModel';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import Switch from 'react-switch';

import FlightResultFilterSlider from '../Result/FlightResultFilterSlider';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faGlobe, faPlane, faBars } from '@fortawesome/free-solid-svg-icons';
import { getElapsedStr } from 'utils/flight/flight';
import { faFilterSlash } from '@fortawesome/sharp-duotone-solid-svg-icons';
import Dropdown from 'components/shared/Dropdown';
import { dateFormat } from 'utils/shared/utils';

interface FlightPairsFilterProps {
  filter: FlightFilterModel;
  setFilter: (filter: FlightFilterModel) => void;
  onClickReset: () => void;
  flightsLength: number;
  flightsVisibleLength: number;
}

const FlightPairsFilter: FC<FlightPairsFilterProps> = ({
  filter,
  setFilter,
  onClickReset,
  flightsLength,
  flightsVisibleLength,
}) => {
  const [t] = useTranslation();
  const [closeOnClickDate1, setCloseOnClickDate1] = useState<Date>();
  const [closeOnClickDate2, setCloseOnClickDate2] = useState<Date>();
  const [closeOnClickDate3, setCloseOnClickDate3] = useState<Date>();
  const [localFreeText, setLocalFreeText] = useState(filter.freeText);
  const timerFreetext = useRef<NodeJS.Timeout>();

  useEffect(() => {
    setLocalFreeText(filter.freeText);
  }, [filter.freeText]);

  const onClickCheckbox = (
    value: boolean,
    index: number,
    option: 'stops' | 'airlines' | 'airports' | 'flightTypes' | 'baggage'
  ) => {
    if (option === 'stops' && filter.stops[index].disabled) return;

    const newStops = [...filter[option]];
    newStops[index].checked = value;
    setFilter({ ...filter, [option]: newStops });
  };

  const onFreeTextChange = (value: string) => {
    setLocalFreeText(value);
    if (timerFreetext.current) {
      clearTimeout(timerFreetext.current);
    }

    timerFreetext.current = setTimeout(() => {
      setFilter({ ...filter, freeText: value });
    }, 300);
  };

  const label = (entity: any, tr?: boolean) => {
    return (
      <span className="flex-between w100p text-lh-sm">
        {tr ? t(entity.label) : entity.label}
        <span className="text-sm flight-filter-text">{entity.minPrice}</span>
      </span>
    );
  };

  const showClearFilter = () => {
    const depTimesChanged =
      filter.departureOutboundTime &&
      filter.departureOutboundTime &&
      filter.arrivalOutboundTime &&
      filter.arrivalOutboundTime &&
      (filter.departureOutboundTime.value[0] !== filter.departureOutboundTime.min ||
        filter.departureOutboundTime.value[1] !== filter.departureOutboundTime.max ||
        filter.arrivalOutboundTime.value[0] !== filter.arrivalOutboundTime.min ||
        filter.freeText !== '' ||
        filter.arrivalOutboundTime.value[1] !== filter.arrivalOutboundTime.max);

    const arrTimesChanged =
      filter.isInbound &&
      filter.departureInboundTime &&
      (filter.departureInboundTime.value[0] !== filter.departureInboundTime.min ||
        filter.departureInboundTime.value[1] !== filter.departureInboundTime.max) &&
      filter.arrivalInboundTime &&
      (filter.arrivalInboundTime.value[0] !== filter.arrivalInboundTime.min ||
        filter.arrivalInboundTime.value[1] !== filter.arrivalInboundTime.max);
    return (
      filter.stops.some((stop) => stop.checked) ||
      filter.airlines.some((airline) => airline.checked) ||
      filter.airports.some((airport) => airport.checked) ||
      filter.flightTypes.some((flightType) => flightType.checked) ||
      filter.price.value[0] !== filter.price.min ||
      filter.price.value[1] !== filter.price.max ||
      filter.duration.value[0] !== filter.duration.min ||
      filter.duration.value[1] !== filter.duration.max ||
      filter.stopDuration.value[0] !== filter.stopDuration.min ||
      filter.stopDuration.value[1] !== filter.stopDuration.max ||
      !filter.baggage.every((bag) => bag.checked) ||
      depTimesChanged ||
      arrTimesChanged
    );
  };

  return (
    <div className="fl-pair-filter">
      <section className="fl-pair-filter-section" style={{ width: 120 }}>
        <FormGroup
          movablePlaceholder={t('filter.free_text')}
          label={t('filter.free_text')}
          movablePlaceholderSm
          value={localFreeText}
          onChange={onFreeTextChange}
        />
      </section>
      <section className="fl-pair-filter-section">
        <div className="fl-pair-stops">
          <div
            className={
              'stop-s' +
              (filter.stops.find((stop) => stop.number === 0)?.disabled ? ' disabled' : '') +
              (filter.stops.find((stop) => stop.number === 0)?.checked ? ' active' : '')
            }
            onClick={() =>
              onClickCheckbox(!filter.stops.find((stop) => stop.number === 0)?.checked, 0, 'stops')
            }
          >
            <FontAwesomeIcon icon={faArrowRight as any} color="var(--color-success)" />
          </div>
          <div
            className={
              'stop-s' +
              (filter.stops.find((stop) => stop.number === 1)?.disabled ? ' disabled' : '') +
              (filter.stops.find((stop) => stop.number === 1)?.checked ? ' active' : '')
            }
            onClick={() =>
              onClickCheckbox(
                !filter.stops.find((stop) => stop.number === 1)?.checked,
                filter.stops.findIndex((stop) => stop.number === 1),
                'stops'
              )
            }
          >
            1
          </div>
          <div
            className={
              'stop-s color-danger ' +
              (filter.stops.find((stop) => stop.number === 2)?.disabled ? ' disabled' : '') +
              (filter.stops.find((stop) => stop.number === 2)?.checked ? ' active' : '')
            }
            onClick={() =>
              onClickCheckbox(
                !filter.stops.find((stop) => stop.number === 2)?.checked,
                filter.stops.findIndex((stop) => stop.number === 2),
                'stops'
              )
            }
          >
            2+
          </div>
        </div>
      </section>

      <section className="fl-pair-filter-section">
        <Dropdown
          opener={<FontAwesomeIcon icon={faGlobe as any} />}
          byClick
          closeOnClickDate={closeOnClickDate1}
        >
          <section className="fl-pair-filter-air">
            <div className="text-bold mb-15">{t('filter.airports')}</div>
            {filter.airports.map((airport, index) => (
              <Checkbox
                key={airport.code}
                htmlLabel={label(airport)}
                checked={airport.checked}
                onChange={(value) => onClickCheckbox(value, index, 'airports')}
                className="mb-10 text-sm"
              />
            ))}
            <div>
              <Button title={t('close')} onClick={() => setCloseOnClickDate1(new Date())} />
            </div>
          </section>
        </Dropdown>
      </section>
      <section className="fl-pair-filter-section">
        <Dropdown
          opener={<FontAwesomeIcon icon={faPlane as any} />}
          byClick
          closeOnClickDate={closeOnClickDate2}
        >
          <section className="fl-pair-filter-air">
            <div className="text-bold mb-15">{t('filter.airlines')}</div>
            {filter.airlines.map((airline, index) => (
              <Checkbox
                key={airline.code}
                htmlLabel={label({ ...airline, label: airline.label || airline.code })}
                checked={airline.checked}
                onChange={(value) => onClickCheckbox(value, index, 'airlines')}
                className="mb-10 text-sm"
              />
            ))}
            <div>
              <Button title={t('close')} onClick={() => setCloseOnClickDate2(new Date())} />
            </div>
          </section>
        </Dropdown>
      </section>
      <Tooltip content={'Departure Time'}>
        <section className="fl-pair-filter-section mw-95">
          {filter.departureOutboundTime && (
            <FlightResultFilterSlider
              filter={filter}
              setFilter={setFilter}
              minStr={dateFormat(filter.departureOutboundTime.value[0], 'HH:mm')}
              maxStr={dateFormat(filter.departureOutboundTime.value[1], 'HH:mm')}
              type="departureOutboundTime"
              scale={0.8}
              onlyLtr={true}
            />
          )}
        </section>
      </Tooltip>
      <Tooltip content={'Arrival Time'}>
        <section className="fl-pair-filter-section mw-95">
          {filter.arrivalOutboundTime && (
            <FlightResultFilterSlider
              filter={filter}
              setFilter={setFilter}
              minStr={dateFormat(filter.arrivalOutboundTime.value[0], 'HH:mm')}
              maxStr={dateFormat(filter.arrivalOutboundTime.value[1], 'HH:mm')}
              type="arrivalOutboundTime"
              scale={0.8}
              onlyLtr={true}
            />
          )}
        </section>
      </Tooltip>

      <Tooltip content={'Connection time'}>
        <section className="fl-pair-filter-section mw-95">
          <FlightResultFilterSlider
            filter={filter}
            setFilter={setFilter}
            minStr={getElapsedStr(t, filter.stopDuration.value[0], true)}
            maxStr={getElapsedStr(t, filter.stopDuration.value[1], true)}
            type="stopDuration"
            scale={0.8}
            onlyLtr={true}
          />
        </section>
      </Tooltip>
      <Tooltip content={'Duration'}>
        <section className="fl-pair-filter-section mw-95">
          <FlightResultFilterSlider
            filter={filter}
            setFilter={setFilter}
            minStr={getElapsedStr(t, filter.duration.value[0], true)}
            maxStr={getElapsedStr(t, filter.duration.value[1], true)}
            type="duration"
            scale={0.8}
            onlyLtr={true}
          />
        </section>
      </Tooltip>
      <section className="fl-pair-filter-section">
        <Dropdown
          opener={
            <span>
              <FontAwesomeIcon icon={faBars as any} />
              <span style={{ position: 'absolute', fontSize: 9, top: 1, right: 1 }}>
                ({filter.flightTypes.length})
              </span>
            </span>
          }
          byClick
          closeOnClickDate={closeOnClickDate3}
        >
          <section className="fl-pair-filter-air">
            <div className="text-bold mb-15">{t('flight.s')}</div>
            {filter.flightTypes.map((type, index) => (
              <Checkbox
                key={type.code}
                htmlLabel={label({ ...type, label: type.label || 'שכר' }, true)}
                checked={type.checked}
                onChange={(value) => onClickCheckbox(value, index, 'flightTypes')}
                className="mb-10 text-sm"
              />
            ))}
            <div>
              <Button title={t('close')} onClick={() => setCloseOnClickDate3(new Date())} />
            </div>
          </section>
        </Dropdown>
      </section>

      <section className="fl-pair-filter-section display-flex">
        <div className="flex-center">
          <Switch
            checked={filter.baggage.some((bag) => bag.checked)}
            onChange={() => {
              const newBaggage = [
                { ...filter.baggage[0], checked: false },
                { ...filter.baggage[1], checked: !filter.baggage[1].checked },
              ];
              setFilter({
                ...filter,
                baggage: newBaggage,
              });
            }}
            uncheckedIcon={false}
            checkedIcon={false}
            activeBoxShadow={undefined}
            checkedHandleIcon={<div className="react-switcher">Bag</div>}
            uncheckedHandleIcon={<div className="react-switcher">All</div>}
            height={20}
            width={40}
          />
        </div>
      </section>

      <div className="fl-pair-filter-section">
        <Button
          className={'btn ' + (showClearFilter() ? 'btn-danger' : 'btn-default')}
          title=""
          onClick={onClickReset}
          icon={faFilterSlash}
        />
      </div>
      <section
        className="fl-pair-filter-section display-flex flex-one flex-wrap"
        style={{ placeContent: 'flex-end' }}
      >
        <div className="text-xsm">
          {flightsVisibleLength}
          {flightsVisibleLength !== flightsLength && `/${flightsLength}`}
        </div>
      </section>
    </div>
  );
};

export default FlightPairsFilter;
